import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import "./products.css";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getGuestCart, setGuestCart } from "../Cart/cartStorage";

function Products() {
  const { categoryName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [categoryId, setCategoryId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const limit = 10;

  const searchQuery =
    new URLSearchParams(location.search).get("search") || "";

  // ================= ADD TO CART =================
  const handleAddToCart = (item, e) => {
    e.stopPropagation();
    const token = localStorage.getItem("access");

    if (token) {
      axiosInstance
        .post("/cart/cart/", { product: item.id, quantity: 1 })
        .then(() => {
          toast.success("Added to cart ❤️");
          window.dispatchEvent(new Event("cartUpdated"));
          navigate("/cart");
        })
        .catch(() => toast.error("Failed ❌"));
    } else {
      const cart = getGuestCart();

      const existing = cart.find(
        (c) => c.product_id === item.id && !c.size
      );

      if (existing) {
        existing.quantity = existing.quantity + 1;
      } else {
        cart.push({
          product_id: item.id,
          quantity: 1,
          price: item.sizes?.length ? item.sizes[0].price : item.price,
          title: item.product_title,
          image: item.images?.[0]?.image || "",

          size: null,
          color: null,
          number_plate: "",
          product_sizes: item.sizes || [],
          product_colors: item.colors || [],
          category: item.category?.map(c => c.name).join(", ")
        });
      }

      setGuestCart(cart);
      window.dispatchEvent(new Event("cartUpdated"));
      toast.success("Added to cart ❤️");
      navigate("/cart");
    }
  };

  // ================= FETCH PRODUCTS =================
  const fetchProducts = async (newOffset = 0) => {
    if (loading || !hasMore) return;

    setLoading(true);

    let url = `/products/?limit=${limit}&offset=${newOffset}`;

    if (categoryId) url += `&category=${categoryId}`;
    if (selectedBrand) url += `&brand=${selectedBrand}`;
    if (selectedModel) url += `&bike_model=${selectedModel}`;
    if (searchQuery) url += `&search=${searchQuery}`;

    try {
      const res = await axiosInstance.get(url);
      const newData = res.data.results;

      if (!Array.isArray(newData)) {
        console.error("Invalid API response:", res.data);
        setLoading(false);
        return;
      }

      setProducts((prev) =>
        newOffset === 0 ? newData : [...prev, ...newData]
      );

      if (newData.length < limit) {
        setHasMore(false);
      }

      setOffset(newOffset + limit);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  // ================= LOAD CATEGORY =================
  useEffect(() => {
    const loadCategory = async () => {
      if (!categoryName || categoryName.toLowerCase() === "all") {
        setCategoryId(null);
        return;
      }

      try {
        const res = await axiosInstance.get("/categories/");
        const category = res.data.find(
          (c) =>
            c.name.toLowerCase() === categoryName.toLowerCase()
        );

        setCategoryId(category ? category.id : null);
      } catch (error) {
        console.log(error);
      }
    };

    loadCategory();
  }, [categoryName]);

  // ================= RESET & FETCH =================
  useEffect(() => {
    const isAll = categoryName?.toLowerCase() === "all" || !categoryName;
    if (!isAll && !categoryId) return;

    const initialFetch = async () => {
      setLoading(true); // START LOADING FIRST
      setProducts([]);  // THEN CLEAR LIST
      setOffset(0);
      setHasMore(true);
      window.scrollTo(0, 0); // ✅ Add this line for better UX
      let url = `/products/?limit=${limit}&offset=0`;
      if (categoryId) url += `&category=${categoryId}`;
      if (selectedBrand) url += `&brand=${selectedBrand}`;
      if (selectedModel) url += `&bike_model=${selectedModel}`;
      if (searchQuery) url += `&search=${searchQuery}`;

      try {
        const res = await axiosInstance.get(url);
        setProducts(res.data.results);
        setHasMore(res.data.results.length === limit);
        setOffset(limit);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false); // STOP LOADING LAST
      }
    };

    initialFetch();
  }, [categoryId, selectedBrand, selectedModel, searchQuery, categoryName]);

  // ================= FETCH BRANDS =================
  useEffect(() => {
if (categoryId) {
  axiosInstance
    .get(`/brands/?category=${categoryId}`)
    .then((res) => setBrands(res.data))
    .catch((err) => console.log(err));
} else {
  axiosInstance
    .get(`/brands/`)
    .then((res) => setBrands(res.data))
    .catch((err) => console.log(err));
}
  }, [categoryId]);
  // Inside your Cart component lo
  // ================= FETCH MODELS =================
  useEffect(() => {
    if (selectedBrand) {
      axiosInstance
        .get(`/bike-models/?brand=${selectedBrand}`)
        .then((res) => setModels(res.data))
        .catch((err) => console.log(err));
    } else {
      setModels([]);
      setSelectedModel("");
    }
  }, [selectedBrand]);

  // ================= INFINITE SCROLL =================
  useEffect(() => {
    const handleScroll = () => {
      if (loading || !hasMore) return;

      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= fullHeight - 100) {
        fetchProducts(offset);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loading, hasMore, offset]);

  return (
    <div className="product-container">
      <h2 className="product-title">
        <span>
          {categoryName?.toLowerCase() === "all"
            ? "ALL"
            : categoryName
              ? categoryName.toUpperCase()
              : "OUR"}{" "}
          PRODUCTS
        </span>
      </h2>
      <button
        className="mobile-filter-btn"
        onClick={() => setIsSidebarOpen(true)}
      >
        🔍 Filter
      </button>
      
      <div className="products-layout-wrapper">
        {/* SIDEBAR: 20% WIDTH */}
        {isSidebarOpen && (
  <div
    className="sidebar-overlay"
    onClick={() => setIsSidebarOpen(false)}
  ></div>
)}
{/* {isSidebarOpen && (
  <button
    className="sidebar-close-btn"
    onClick={() => setIsSidebarOpen(false)}
  >
    ✕ Close
  </button>
)} */}
        <aside className={`sidebar ${isSidebarOpen ? "open" : ""}`}>

          <h4 className="sidebar-heading">Brands</h4>
          <div className="sidebar-filter">
            {loading && brands.length === 0 ? (
              [1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="skeleton skeleton-brand-btn"></div>
              ))
            ) : (
              <>
                <button
                  className={`model-btn ${selectedBrand === "" ? "active" : ""}`}
                  onClick={() => {
                    setSelectedBrand("");
                    setSelectedModel("");
                  }}
                >
                  All Brands
                </button>
                {brands.length === 0 ? (
  <p style={{fontSize:"14px", color:"#777"}}>No brands available</p>
) : (
  brands.map((b) => (
    <button
      key={b.id}
      className={`model-btn ${selectedBrand === b.id ? "active" : ""}`}
      onClick={() => {
        setSelectedBrand(b.id);
        setSelectedModel("");
      }}
    >
      {b.name}
    </button>
  ))
)}
              </>
            )}
          </div>

          {/* Models Section */}
          {selectedBrand && models.length > 0 && (
            <>
              <h4 className="sidebar-heading" style={{ marginTop: "20px" }}>Models</h4>
              <div className="sidebar-filter">
                {loading && models.length === 0 ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} className="skeleton skeleton-brand-btn"></div>
                  ))
                ) : (
                  <>
                    <button
                      className={`model-btn ${selectedModel === "" ? "active" : ""}`}
                      onClick={() => setSelectedModel("")}
                    >
                      All Models
                    </button>
                    {models.map((m) => (
                      <button
                        key={m.id}
                        className={`model-btn ${selectedModel === m.id ? "active" : ""}`}
                        onClick={() => setSelectedModel(m.id)}
                      >
                        {m.name}
                      </button>
                    ))}
                  </>
                )}
              </div>
            </>
          )}
        </aside>

        {/* PRODUCTS CONTENT: 80% WIDTH */}
        <main className="products-content">
          <div className="card-grid">
            {/* Initial Loading Skeletons */}
            {loading && products.length === 0 && (
              Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="skeleton-card">
                  <div className="skeleton skeleton-img"></div>
                  <div className="skeleton skeleton-text"></div>
                  <div className="skeleton skeleton-price"></div>
                  <div className="skeleton skeleton-btn"></div>
                </div>
              ))
            )}

            {/* Product List */}
            {products.map((item) => (
              <div
                key={item.id}
                className="product-card"
                onClick={() => navigate(`/product/${item.id}`)}
              >
                {item.total_quantity_sold >= 20 && (
                  <div className="trending-badge"><span>🔥</span> Trending</div>
                )}
                <img src={item.images?.[0]?.image || ""} className="card-img" alt={item.product_title} />
                <h3 className="card-name">{item.product_title}</h3>
                <div className="price-section">
                  {item.sizes && item.sizes.length > 0 ? (() => {
                    const price = Math.min(...item.sizes.map(s => s.price));
                    const discount = 20;

                    const originalPrice = Math.round(price * (1 + discount / 100));

                    return (
                      <>
                        <span className="discounted-price">₹{price}</span>

                        <span className="original-price">₹{originalPrice}</span>

                        <span className="discount-badge">
                          {discount}% OFF
                        </span>
                      </>
                    );
                  })() : (() => {

                    const price = Number(item.price);
                    const discount = 20;

                    const originalPrice = Math.round(price * (1 + discount / 100));

                    return (
                      <>
                        <span className="discounted-price">₹{price}</span>

                        <span className="original-price">₹{originalPrice}</span>

                        <span className="discount-badge">
                          {discount}% OFF
                        </span>
                      </>
                    );
                  })()}
                </div>
                <button className="add-cart-btn" onClick={(e) => handleAddToCart(item, e)}>
                  <b>Add to Cart</b>
                </button>
              </div>
            ))}
          </div>
          {!loading && products.length === 0 && (
            <div className="empty-state">
              {/* <h3>No products found</h3> */}
            </div>
          )}
        </main>
      </div>
    </div>
  );

}

export default Products;
